/**
 * rankByUsage — orders a picker's options most-used-first, counted from the
 * site's own records at the moment the picker opens (P2-3, picker ranking).
 * Nothing is stored and nothing syncs: the records ARE the usage history, so
 * every rep at a site sees the same order, on any device, after any restart.
 *
 * Count key: String(raw).trim().toUpperCase(), applied identically to the
 * record value and to the option string. Case and surrounding whitespace fold;
 * punctuation and inner spacing do NOT, so 'MG-S', 'MG S' and 'MG S.' are three
 * keys with three counts, by design.
 *
 * Order: descending by count, STABLE — equal counts keep the incoming order,
 * so zero-count options fall to the bottom as one block in their original
 * order (the stored order of a fixed list, the collated order of a location
 * list). The tail is never re-sorted.
 *
 * Returns the ORIGINAL option strings in a new array. It reorders; it never
 * rewrites a display string, and it never mutates `options` or `records`.
 *
 * Emptiness and the record read belong to getFieldUniqueValues alone — the
 * same function that builds the location options — so a counted value is, by
 * definition, a listed value. No second emptiness rule is applied here.
 *
 * PURE. No React, no Dexie, no storage.
 */
import { getFieldUniqueValues } from './filterRecords';

const countKey = (value) => String(value).trim().toUpperCase();

export function rankByUsage(options, records, fieldKey) {
  const counts = new Map();
  for (const record of Array.isArray(records) ? records : []) {
    // One record at a time on purpose: the plural function is the ONE accessor and emptiness rule, so its [value] or [] is this record's count.
    for (const value of getFieldUniqueValues([record], fieldKey)) {
      const key = countKey(value);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }
  // Decorate → sort → undecorate. The index is the stability guarantee.
  return options
    .map((option, index) => ({ option, index, count: counts.get(countKey(option)) || 0 }))
    .sort((a, b) => b.count - a.count || a.index - b.index)
    .map((entry) => entry.option);
}
